import { FC } from 'react';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { FileUpload } from '@components/fileUpload';
import { FieldTypeEnum } from '@libs/constants';
import { Trans } from '@libs/i18n';
import { FileUploadType } from '@libs/types';

export interface DomainField {
  type: FieldTypeEnum;
  id: string;
  name: string;
  description?: string | null;
  required?: boolean;
  orderIndex: number;
  documentRequirements?: Array<{
    id: string;
    name?: string;
    description?: string | null;
    key?: string;
    acceptedFormats: string[];
    maxSizeBytes: number;
    standardDocument?: {
      id: string;
      name: string;
      description?: string | null;
      key: string;
      documentType?: string | null;
    };
  }>;
}

export interface DomainFieldRendererProps {
  field: DomainField;
  files?: FileUploadType[];
  onAddFiles?: (files: File[]) => void;
  onDeleteFile?: (fileId: string) => void;
  onCancelUpload?: (fileId: string) => void;
  onUndoDelete?: (fileId: string) => void;
  onRetryDelete?: (fileId: string) => void;
  onRetryUpload?: (fileId: string) => void;
  onRemoveFailedUpload?: (fileId: string) => void;
  disabled?: boolean;
  /** Optional render function for additional content below each file item */
  renderExtraContent?: (file: FileUploadType) => React.ReactNode;
  /** Optional render function for an inline badge in each file's meta row. */
  renderFileBadge?: (file: FileUploadType) => React.ReactNode;
  /** Optional predicate — when true for a file, that card shows a moving overlay. */
  isFileMoving?: (file: FileUploadType) => boolean;
  /** True while a file is still queued for or undergoing AI analysis. */
  isFileProcessing?: (file: FileUploadType) => boolean;
}

/**
 * Renders different field types based on field.type
 * Currently supports 'document' type (file upload)
 * Can be extended to support other types like 'checkbox', 'text', 'number', etc.
 */
export const DomainFieldRenderer: FC<DomainFieldRendererProps> = observer(
  ({
    field,
    files = [],
    onAddFiles,
    onDeleteFile,
    onCancelUpload,
    onUndoDelete,
    onRetryDelete,
    onRetryUpload,
    onRemoveFailedUpload,
    disabled = false,
    renderExtraContent,
    renderFileBadge,
    isFileMoving,
    isFileProcessing,
  }) => {
    const { enqueueSnackbar } = useSnackbar();

    // Determine allowed file types and max size from document requirements
    const allowedFileTypes = field.documentRequirements
      ? [...new Set(field.documentRequirements.flatMap((req) =>
        req.acceptedFormats.map((format) => (format.startsWith('.') ? format : `.${format}`)),
      ))]
      : ['.pdf', '.doc', '.docx', '.xlsx', '.xls', '.png', '.jpeg', '.jpg'];

    const maxFileSizeMb = field.documentRequirements
      ? Math.max(...field.documentRequirements.map((req) => req.maxSizeBytes)) / (1024 * 1024)
      : 10;

    const maxFiles = 30;
    // Only count files that are not deleted
    const activeFilesCount = files.filter((file) => file.status !== 'deleted').length;
    const isMaxFilesReached = activeFilesCount >= maxFiles;

    const handleValidationError = (error: {
      type: 'size' | 'type' | 'maxFiles';
      fileName: string;
      fileExtension?: string;
      maxFiles?: number;
    }) => {
      switch (error.type) {
        case 'size':
          enqueueSnackbar(
            <Trans
              i18nKey="fileUpload.errors.fileSizeExceeded"
              values={{ fileName: error.fileName, maxSize: maxFileSizeMb }}
            />,
            { variant: 'error' },
          );
          break;
        case 'type':
          enqueueSnackbar(
            <Trans
              i18nKey="fileUpload.errors.fileTypeNotAllowed"
              values={{
                fileExtension: error.fileExtension,
                allowedTypes: allowedFileTypes.join(', ').toUpperCase(),
              }}
            />,
            { variant: 'error' },
          );
          break;
        case 'maxFiles':
          enqueueSnackbar(
            <Trans
              i18nKey="fileUpload.errors.maxFilesExceeded"
              values={{ maxFiles: error.maxFiles }}
            />,
            { variant: 'error' },
          );
          break;
      }
    };

    switch (field.type) {
      case 'document':
        return (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              width: '100%',
              overflowX: 'hidden',
            }}
          >
            <FileUpload
              sizeVariant="large"
              files={files}
              onAdd={onAddFiles}
              onDelete={onDeleteFile}
              onCancel={onCancelUpload}
              onUndoDelete={onUndoDelete}
              onRetryDelete={onRetryDelete}
              onRetryUpload={onRetryUpload}
              onRemoveFailedUpload={onRemoveFailedUpload}
              onValidationError={handleValidationError}
              disabled={disabled || isMaxFilesReached}
              multiple={true}
              allowedFileTypes={allowedFileTypes}
              maxFileSizeMb={maxFileSizeMb}
              maxFiles={maxFiles}
              renderExtraContent={renderExtraContent}
              renderFileBadge={renderFileBadge}
              isFileMoving={isFileMoving}
              isFileProcessing={isFileProcessing}
            />
          </Box>
        );

      // Future field types can be added here
      case FieldTypeEnum.CHECKMARK:
        return null;

      case FieldTypeEnum.TEXT:
        return null;

      default:
        return null;
    }
  },
);
